import { z } from "zod";
import { SlideElementSchema } from "./draggables";
import { WordBoundarySchema } from "./media";

// ============================================================================
// Slide Type, Skill & Knowledge Area Enums
// ============================================================================

/**
 * @deprecated Superseded by `SlideTemplateEnum`. Retained on slides so already-shipped clients
 * keep resolving it, and removed once they are past the `minimumVersion` gate. Slide content is
 * discriminated by `template` (see `SlideSchema`).
 */
export const SlideTypeEnum = z.enum(["word", "grammar", "reading", "other", "phonics"]);

// Primary slide classification going forward: the language skill a slide trains.
// Named `SlideSkill*` to avoid clashing with the `Skill` domain entity in ./skill.
export const SlideSkillEnum = z.enum([
  "vocabulary",
  "speaking",
  "reading",
  "writing",
  "listening",
  "grammar",
  "phonics",
  "usage",
  "realLifeVocabulary",
  "realLifeConversation",
  "conversation",
]);

// Closed set — report aggregates and charts are keyed on these values.
export const SlideDomainEnum = z.enum([
  "speaking",
  "listening",
  "reading",
  "grammar",
  "writing",
  "wordRecognition",
]);

// Unified knowledge area enum, retained as slide passthrough only — `template` selects
// `slideData` now (see `SlideSchema`). The union of the per-type areas below, minus the values
// that are no longer used (`usage` everywhere, `meaning` on grammar, `vocabulary` on "other").
export const KnowledgeAreaEnum = z.enum([
  "pronunciation",
  "meaning",
  "vocabulary",
  "form",
  "sentence",
  "response",
  "fluency",
  "comprehension",
  "encode",
  "decode",
  "reading",
  "writing",
  "listening",
  "speaking",
]);

// Knowledge area enums for each slide type
// @deprecated Kept for existing (type-keyed) consumers; prefer `KnowledgeAreaEnum`.
export const WordKnowledgeAreaEnum = z.enum(["pronunciation", "usage", "meaning", "vocabulary"]);
export const GrammarKnowledgeAreaEnum = z.enum([
  "form",
  "meaning",
  "usage",
  "sentence",
  "response",
]);
export const ReadingKnowledgeAreaEnum = z.enum(["fluency", "comprehension"]);
export const PhonicsKnowledgeAreaEnum = z.enum(["encode", "decode"]);
export const OtherKnowledgeAreaEnum = z.enum([
  "reading",
  "writing",
  "listening",
  "speaking",
  "vocabulary",
]);

export const knowledgeAreaConfig = {
  word: WordKnowledgeAreaEnum,
  grammar: GrammarKnowledgeAreaEnum,
  reading: ReadingKnowledgeAreaEnum,
  phonics: PhonicsKnowledgeAreaEnum,
  other: OtherKnowledgeAreaEnum,
};

export type KnowledgeAreaBySlide<T extends keyof typeof knowledgeAreaConfig> = z.infer<
  (typeof knowledgeAreaConfig)[T]
>;

// ============================================================================
// Shared Schemas & Helper Types
// ============================================================================

const PronunciationAudioCacheSchema = z.object({
  hintAudioUrl: z.string().nullable().optional(),

  syllableAudioUrls: z
    .union([z.record(z.string(), z.string()), z.map(z.string(), z.string())])
    .nullable()
    .optional(),
});

// Helper to convert Unix timestamp (number) or ISO string to Date
const dateFromTimestamp = z.union([z.date(), z.number(), z.string()]).transform((val) => {
  if (val instanceof Date) {
    return val;
  }
  return new Date(val);
});

const SyllableUnitSchema = z.object({
  syllable: z.string(),
  grapheme: z.string(),
});

export type SyllableUnit = z.infer<typeof SyllableUnitSchema>;

/** One underlying Media House/Azure sound making up part of a displayed syllable, e.g. "bout" = "b"+"ou"+"t". */
export const SoundUnitAudioSchema = z.object({
  grapheme: z.string(),
  ipa: z.string(),
  // Media House's stable phoneme identity for this sound, or null when Azure-synthesized.
  phonemeId: z.string().nullable(),
  audioUrl: z.string(),
  audioDuration: z.string(),
  audioId: z.string(),
  source: z.enum(["phonics", "azure"]),
});

export type SoundUnitAudio = z.infer<typeof SoundUnitAudioSchema>;

/**
 * Schema for syllable audio data
 * Generated by synthesizeSyllables mutation and stored in WordSlideData
 */
export const SyllableAudioSchema = z.object({
  syllable: z.string(),
  audioUrl: z.string(),
  audioDuration: z.string(),
  audioId: z.string(),
  // Empty on one-sound syllables (stored null or absent on older rows); ordered sound units otherwise.
  soundUnits: z
    .array(SoundUnitAudioSchema)
    .nullish()
    .transform((units) => units ?? []),
});

export type SyllableAudio = z.infer<typeof SyllableAudioSchema>;

export const MediaSchema = z.object({
  src: z.string().nullable(),
  id: z.string().nullable(),
  filename: z.string().nullable(),
});

export const ImageSchema = MediaSchema.extend({
  displaySrc: z.string().nullable().optional(),
});

export const AudioSchema = MediaSchema.extend({
  voice: z.string().optional(),
  // Per-word TTS timing, used to highlight text in sync with audio playback. Optional/nullable:
  // most existing audio was generated before this was captured (see media.ts's WordBoundarySchema,
  // the source of truth for the shape — this is the same field on the slide-embedded audio
  // reference rather than the raw stored-media record).
  wordBoundaries: z.array(WordBoundarySchema).nullable().optional(),
});

/** Progress of automatic subtitle generation, kicked off when a video is uploaded. */
export const SubtitleStatusEnum = z.enum(["generating", "done", "failed"]);
export type SubtitleStatus = z.infer<typeof SubtitleStatusEnum>;

const IntroductionVideoMediaSchema = MediaSchema.extend({
  subtitles: z
    .array(
      z.object({
        word: z.string(),
        start: z.number(),
        end: z.number(),
        cueIndex: z.number().nullable().optional(),
      })
    )
    .nullable()
    .optional(),
  subtitleStatus: SubtitleStatusEnum.nullable().optional(),
  subtitlePosition: z.string().nullable().optional(),
  subtitlePreset: z.string().nullable().optional(),
  subtitleFontScale: z.number().nullable().optional(),
  subtitleOffset: z.number().nullable().optional(),
  trimStart: z.number().nullable().optional(),
  trimEnd: z.number().nullable().optional(),
});

const nullMedia = { src: null, id: null, filename: null };

// Spell check status enum for passages
export const SpellCheckStatusSchema = z.enum(["unchecked", "passed", "failed"]);
export type SpellCheckStatus = z.infer<typeof SpellCheckStatusSchema>;

/**
 * Schema for video checkpoint data
 * Used in grammar - response slide
 */

export const AIGradingModeEnum = z.enum(["strictMode", "contextMode", "grammarMode"]);

export type AIGradingMode = z.infer<typeof AIGradingModeEnum>;

export const VideoCheckpointSchema = z.object({
  timestamp: z.number(),
  question: z.string().nullable(),
  answer: z.string().nullable(),
  gradingMode: AIGradingModeEnum,
  skipTo: z.number().nullable().default(null),
  image: MediaSchema.default(nullMedia),
});

export type VideoCheckpoint = z.infer<typeof VideoCheckpointSchema>;

export const VideoSkipZoneSchema = z.object({
  start: z.number(),
  end: z.number(),
});

export type VideoSkipZone = z.infer<typeof VideoSkipZoneSchema>;

export const VideoSubtitleSchema = z.object({
  text: z.string(),
  startTime: z.number(),
  endTime: z.number(),
  position: z.string().nullable().optional(),
  preset: z.string().nullable().optional(),
  fontScale: z.number().nullable().optional(),
  offset: z.number().nullable().optional(),
});

// ============================================================================
// Slide Data Schemas (type-specific content)
// ============================================================================

export const WordSlideDataSchema = z.object({
  word: z.string().nullable(),
  sentence: z.string().nullish(),
  syllable: z.string().nullable(),
  syllableAudios: z.array(SyllableAudioSchema),
  wordAudio: MediaSchema,
  sentenceAudio: MediaSchema.nullish(),
  image: ImageSchema,
  isVideoWithImage: z.boolean().default(false),
  recordOnly: z.boolean().nullish(),
});

// Per-word timings for a passage clip whose soundtrack IS the passage read aloud, in the same
// milliseconds as AudioSchema.wordBoundaries, so the karaoke highlight can follow either source.
const PassageVideoSchema = MediaSchema.extend({
  wordBoundaries: z.array(WordBoundarySchema).nullable().optional(),
});

const PassageSchema = z.object({
  id: z.string().uuid("Invalid UUID format").nullable(),
  text: z.string().nullable(),
  audio: AudioSchema.nullable(),
  image: ImageSchema.nullable(),
  video: PassageVideoSchema.nullable().default(null),
  // The passage shows EITHER its image or its video, never both, but keeps both authored: a build
  // that predates video reads only `image`, so clearing it would blank the passage on older apps.
  // An authored passage is image + audio, or video alone: a clip's own soundtrack is the model
  // read, so video passages carry no `audio`.
  mediaType: z.enum(["image", "video"]).catch("image").default("image"),
  spellCheckStatus: SpellCheckStatusSchema.default("unchecked"),
  whitelist: z.array(z.string()).default([]),
});

/**
 * A single quiz question attached after a reading passage or an "other" canvas slide. Currently
 * only authored on `fluency` reading slides (the editor gates it), but the field also lives on
 * `OtherSlideDataSchema` for the same post-content mini-quiz flow. Question shape mirrors
 * `PhonicsEncodeSlideDataSchema` for consistency. These quizzes are NOT independent slides: they
 * never enter the slide array / FSRS queue. The parent slide owns them — its FSRS review re-runs
 * the whole set, and its single score is the average of the content score and every quiz score.
 */
export const SubQuizSchema = z.object({
  id: z.string(),
  question: z.string().default(""),
  questionMode: z.enum(["multiple_choice", "fill_in_blank"]).default("multiple_choice"),
  options: z.array(z.string()).default([]),
  correctAnswer: z.string().default(""),
  image: ImageSchema.nullish(),
});

export const ReadingSlideDataSchema = z.object({
  passages: z.array(PassageSchema),
  isTextOnly: z.boolean(),
  recordOnly: z.boolean().nullish(),
  // Post-reading mini-quizzes. Defaults to [] so existing reading slides parse unchanged.
  subQuizzes: z.array(SubQuizSchema).default([]),
});

export const OtherSlideDataSchema = z.object({
  text: z.string().nullable(),
  // Post-content mini-quizzes, mirrors ReadingSlideDataSchema.subQuizzes. Defaults to [] so
  // existing "other" slides parse unchanged.
  subQuizzes: z.array(SubQuizSchema).default([]),
});

export const PhonicsEncodeSlideDataSchema = z.object({
  audio: MediaSchema.default(nullMedia),
  phoneme: z.string().nullable().default(null),
  questionMode: z.enum(["multiple_choice", "fill_in_blank"]).optional().default("multiple_choice"),
  correctAnswer: z.string().nullable().optional().default(null),
  options: z.array(z.string()).default([]),
});

export const PhonicsDecodeSlideDataSchema = z.object({
  audio: MediaSchema.default(nullMedia),
  phoneme: z.string().nullable().optional().default(null),
  /** Media House phoneme behind the chosen audio — resolves which sound to score */
  phonemeId: z.string().nullable().optional().default(null),
});

export const GrammarSlideDataSchema = z.object({
  text: z.string().nullable(),
  image: ImageSchema.nullable(),
  audio: MediaSchema.nullable(),
  correctAnswer: z.string().nullable(),
});

export const GrammarResponseSlideDataSchema = z.object({
  mediaType: z.enum(["video", "audio"]).default("video"),
  video: MediaSchema,
  audio: MediaSchema.default(nullMedia),
  checkpoints: z.array(VideoCheckpointSchema),
  subtitles: z.array(VideoSubtitleSchema).default([]),
  firstQuestionTime: z.number(),
});

// How a student is allowed to answer. Unset/empty means mic, which is what an app build
// predating this field does — a write-only slide still shows a mic there.
export const AnswerModeEnum = z.enum(["mic", "write"]);
export type AnswerMode = z.infer<typeof AnswerModeEnum>;

export const WordVocabularySlideDataSchema = z.object({
  image: ImageSchema.default(nullMedia),
  video: MediaSchema.default(nullMedia),
  audio: AudioSchema.default(nullMedia),
  correctAnswer: z.string().default("Your Answer"),
  instructionText: z.string().nullable().default(null),
  // The card shows EITHER an image or a video, never both. `.catch` maps the legacy
  // "audio" value (written before this field was wired to anything) onto the image
  // card so existing slides keep parsing.
  mediaType: z.enum(["image", "video"]).catch("image").default("image"),
  gradingMode: AIGradingModeEnum.default("strictMode"),
  recordOnly: z.boolean().nullish(),
  answerModes: z.array(AnswerModeEnum).nullish(),
});

export const HandWritingSlideDataSchema = z.object({
  image: ImageSchema.default(nullMedia),
  text: z.array(z.string()).default([]),
  // Index-aligned with `text`; a missing slot means that word has no audio.
  textAudios: z.array(MediaSchema).default([]),
});

// The semantic category of a deck word, chosen so the conversation can ask the
// RIGHT question ("What animal is this?" not "What word is this?"). Cached per
// word because the same word can differ by lesson ("chicken" = food in one, an
// animal in another).
export const WordCategoryEnum = z.enum([
  "animal",
  "food",
  "drink",
  "number",
  "color",
  "family",
  "job",
  "place",
  "action",
  "sound",
  "expression",
  "other",
]);
export type WordCategory = z.infer<typeof WordCategoryEnum>;

// One structured entry per deck word. `visualConcept` = what the image depicts
// (what vision validates); `expectedAnswer` = the authored target the child says
// (server-authored from the deck word, NEVER the classifier — classification must
// not silently change what the lesson teaches). For an ordinary card the two are
// equal. `elicitationQuestion` is the LLM's short per-word question (validated),
// or null → the category phrase map → "What is this?".
export const ConversationMetaEntrySchema = z.object({
  category: WordCategoryEnum,
  visualConcept: z.string(),
  expectedAnswer: z.string(),
  elicitationQuestion: z.string().nullable().optional(),
});
export type ConversationMetaEntry = z.infer<typeof ConversationMetaEntrySchema>;

// Server-owned per-slide classification of the deck, computed at edit time and
// shipped with the slide so a WARM opener is category-aware with no round-trip.
// `contentHash` = normalized wordFocus+grammarFocus+classifierVersion+model, so a
// stale async recompute can be discarded (compare-and-set) and drift triggers a
// recompute.
export const ConversationMetaSchema = z.object({
  entries: z.record(z.string(), ConversationMetaEntrySchema),
  contentHash: z.string(),
  classifierVersion: z.number(),
  model: z.string(),
});
export type ConversationMeta = z.infer<typeof ConversationMetaSchema>;

export const SlideLimitationSchema = z.object({
  grammarLimit: z.string().nullable().optional(),
  grammarFocus: z.string().nullable().optional(),
  wordLimit: z.string().nullable().optional(),
  wordFocus: z.string().nullable().optional(),
  // Staging fields written by the normalize-grammar-focus migration: an AI-suggested rewrite of
  // grammarFocus into the underline format, surfaced in the editor for human review/approval.
  grammarFocusNormalized: z.string().nullable().optional(),
  grammarFocusNeedsReview: z.boolean().nullable().optional(),
  // Server-owned conversation metadata (categories + elicit questions per deck
  // word). Preserved across client limitation edits (the client fragment doesn't
  // send it back) — see slide.service updateSlide. `.catch(null)` degrades a
  // drifted/legacy shape to null instead of failing the WHOLE slide's parse.
  conversationMeta: ConversationMetaSchema.nullable().optional().catch(null),
});

export type SlideLimitation = z.infer<typeof SlideLimitationSchema>;

// ============================================================================
// Slide Schemas (Base + Type-Specific)
// ============================================================================

// Base schema with all common fields (used by all slide types)
const BaseSlideSchema = z.object({
  id: z.string(),
  name: z.string(),
  lessonId: z.string(),
  authorId: z.string(),
  editorId: z.string(),
  isTrash: z.boolean(),
  isDeleted: z.boolean(),
  deletedAt: dateFromTimestamp.nullable().optional(),
  isHidden: z.boolean().optional(),
  createdAt: dateFromTimestamp,
  updatedAt: dateFromTimestamp,
  sortOrder: z.number(),

  // Linked copy metadata
  isLinkedCopy: z.boolean().default(false),
  originalSlideId: z.string().nullable().optional(),

  // Null when this IS a main slide. Nesting is one level deep, and `sortOrder` is scoped to
  // `(lessonId, parentSlideId)` — a subslide is only ever ordered against its own siblings.
  // Optional, not merely nullable: clients that don't select it must still parse.
  parentSlideId: z.string().nullable().optional(),

  // Non-personal fields (synced with original)
  /**
   * @deprecated Superseded by `template`. Passthrough only — no longer selects `slideData`, and
   * OPTIONAL because clients that have stopped selecting it must still parse.
   */
  type: SlideTypeEnum.optional(),
  /** @deprecated Superseded by `template`. Passthrough only, and optional — see `type` above. */
  knowledgeArea: KnowledgeAreaEnum.optional(),
  /** @deprecated Superseded by `tagIds`. */
  skill: SlideSkillEnum.default("vocabulary"),
  // Editor-managed taxonomy (Tag ids). Free-form and multi-valued; never affects scoring.
  tagIds: z.array(z.string()).default([]),
  // Where this slide's score is counted on the student report; `template` never affects scoring.
  domains: z.array(SlideDomainEnum).min(1),
  imageId: z.string().nullable(),
  introductionVideo: IntroductionVideoMediaSchema.nullable().optional(),
  elements: z.array(SlideElementSchema).default([]),
  limitation: SlideLimitationSchema,
  isCompleted: z.boolean(),

  // Optional / cached UI fields (unused by the imageUrl resolver, which always resolves fresh from
  // imageId). Not exposed on the GraphQL Slide type, so clients validating a GraphQL response (e.g.
  // mobile's SlideSchema.safeParse) never receive it — it MUST be optional, not just nullable.
  imageKey: z.string().nullable().optional(),
  pronunciationAudioCache: PronunciationAudioCacheSchema.nullable().optional(),
});

/**
 * `slideData` is selected by `template` alone. Every template maps to exactly one content shape,
 * so this is a true discriminated union — two templates may share a shape (`vocabulary` and
 * `wordCanvas` both carry word data), but no template carries two.
 *
 * `type`/`knowledgeArea` moved to `BaseSlideSchema` as passthrough and no longer constrain which
 * shape is valid. That is deliberately more permissive: a legacy pair with no member today (e.g.
 * `other`/`vocabulary`) failed the whole parse, and now parses on its backfilled template.
 */
const VocabularySlideSchema = BaseSlideSchema.extend({
  template: z.literal("vocabulary"),
  slideData: WordSlideDataSchema,
});

const WordCanvasSlideSchema = BaseSlideSchema.extend({
  template: z.literal("wordCanvas"),
  slideData: WordSlideDataSchema,
});

const ListenRepeatSlideSchema = BaseSlideSchema.extend({
  template: z.literal("listenRepeat"),
  slideData: WordVocabularySlideDataSchema,
});

const ConversationSlideSchema = BaseSlideSchema.extend({
  template: z.literal("conversation"),
  slideData: GrammarSlideDataSchema,
});

const AudioRevealPromptSlideSchema = BaseSlideSchema.extend({
  template: z.literal("audioRevealPrompt"),
  slideData: GrammarSlideDataSchema,
});

const VideoQuestionSlideSchema = BaseSlideSchema.extend({
  template: z.literal("videoQuestion"),
  slideData: GrammarResponseSlideDataSchema,
});

const PassageReadingSlideSchema = BaseSlideSchema.extend({
  template: z.literal("passageReading"),
  slideData: ReadingSlideDataSchema,
});

const FreeformCanvasSlideSchema = BaseSlideSchema.extend({
  template: z.literal("freeformCanvas"),
  slideData: OtherSlideDataSchema,
});

const SoundToSpellingSlideSchema = BaseSlideSchema.extend({
  template: z.literal("soundToSpelling"),
  slideData: PhonicsEncodeSlideDataSchema,
});

const SpellingToSoundSlideSchema = BaseSlideSchema.extend({
  template: z.literal("spellingToSound"),
  slideData: PhonicsDecodeSlideDataSchema,
});

const handWritingSlideSchema = BaseSlideSchema.extend({
  template: z.literal("handWriting"),
  slideData: HandWritingSlideDataSchema,
});

export const SlideSchema: z.ZodDiscriminatedUnion<
  "template",
  [
    typeof VocabularySlideSchema,
    typeof WordCanvasSlideSchema,
    typeof ListenRepeatSlideSchema,
    typeof ConversationSlideSchema,
    typeof AudioRevealPromptSlideSchema,
    typeof VideoQuestionSlideSchema,
    typeof PassageReadingSlideSchema,
    typeof FreeformCanvasSlideSchema,
    typeof SoundToSpellingSlideSchema,
    typeof SpellingToSoundSlideSchema,
    typeof handWritingSlideSchema,
  ]
> = z.discriminatedUnion("template", [
  VocabularySlideSchema,
  WordCanvasSlideSchema,
  ListenRepeatSlideSchema,
  ConversationSlideSchema,
  AudioRevealPromptSlideSchema,
  VideoQuestionSlideSchema,
  PassageReadingSlideSchema,
  FreeformCanvasSlideSchema,
  SoundToSpellingSlideSchema,
  SpellingToSoundSlideSchema,
  handWritingSlideSchema,
]);

// ============================================================================
// Exported Types
// ============================================================================

export type Slide = z.infer<typeof SlideSchema>;
export type PronunciationAudioCache = z.infer<typeof PronunciationAudioCacheSchema>;

export type WordSlideData = z.infer<typeof WordSlideDataSchema>;
export type WordVocabularySlideData = z.infer<typeof WordVocabularySlideDataSchema>;
export type ReadingSlideData = z.infer<typeof ReadingSlideDataSchema>;
export type SubQuiz = z.infer<typeof SubQuizSchema>;
export type OtherSlideData = z.infer<typeof OtherSlideDataSchema>;
export type PhonicsEncodeSlideData = z.infer<typeof PhonicsEncodeSlideDataSchema>;
export type PhonicsDecodeSlideData = z.infer<typeof PhonicsDecodeSlideDataSchema>;
export type GrammarSlideData = z.infer<typeof GrammarSlideDataSchema>;
export type GrammarResponseSlideData = z.infer<typeof GrammarResponseSlideDataSchema>;
export type HandWritingSlideData = z.infer<typeof HandWritingSlideDataSchema>;

export type Passage = z.infer<typeof PassageSchema>;

export type SlideData =
  | WordSlideData
  | ReadingSlideData
  | OtherSlideData
  | PhonicsEncodeSlideData
  | PhonicsDecodeSlideData
  | GrammarSlideData
  | GrammarResponseSlideData
  | WordVocabularySlideData
  | HandWritingSlideData;

export type SlideType = z.infer<typeof SlideTypeEnum>;
export type SlideSkill = z.infer<typeof SlideSkillEnum>;
export type SlideDomain = z.infer<typeof SlideDomainEnum>;
export type KnowledgeArea = z.infer<typeof KnowledgeAreaEnum>;
